import React, { ChangeEvent, SyntheticEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { RequestAdviceDTO } from "../../../../shared";
import { api } from "../../services/api.service";
import { useModal } from "../../store/modal/hooks";
import { styles } from "../../styles";
import { CalculatorIcon } from "../common/Icons";
import { Form, Input, Label, Required, TextArea } from "../common/Input";
import { P } from "../common/Typography";
import { StandardModal } from "./StandardModal";
import { validationRegex } from "./validationRegex";
// FLAT ROOF
import { useFlatRoof } from "../../store/flat-roof/hooks";
// CAVITY WALL
import { useCavityWall } from "../../store/cavity-wall/hooks";
// SLOPED ROOF
import { useSlopedRoof } from "../../store/sloped-roof/hooks";
// VENTILATED WALL
import { useVentilatedWall } from "../../store/ventilated-wall/hooks";
// FLOOR
import { useFloor } from "../../store/floor/hooks";
// SOL
import { useSol } from "../../store/sol/hooks";
// TOIT EN PENTE
import { useToitEnPente } from "../../store/toit-en-pente/hooks";

const PhoneRow = styled.div`
  width: 100%;
  display: flex;
`;
const LocationRow = styled.div`
  width: 100%;
  display: flex;
`;
const LocationContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;
const PhoneContainer = styled.div`
  flex: 2;
  display: flex;
  flex-direction: column;
`;
const SpacerContainer = styled.div`
  width: 20px;
`;
const PostalCodeContainer = styled.div`
  flex: 1;
  width: ${(props) => props.theme.designWidth(120)}px;
  display: flex;
  flex-direction: column;
`;

export const RequestAdvice = () => {
  const { t, i18n } = useTranslation() as any;
  const { closeModal, displayRequestAdviceSuccessModal } = useModal();

  const COUNTRY_OPTIONS = [
    { code: "be", name: t("modal.country.be.title") },
    { code: "nl", name: t("modal.country.nl.title") },
    { code: "fr", name: t("modal.country.fr.title") },
    { code: "de", name: t("modal.country.de.title") },
    { code: "lu", name: t("modal.country.lu.title") },
    { code: "en", name: t("modal.country.en.title") },
  ];

  const appCountry = i18n.language.split("_")[1];
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");
  const [country, setCountry] = useState(COUNTRY_OPTIONS.find((option) => option.code === appCountry)?.code || "en");
  const [province, setProvince] = useState("");
  const [invalidName, setInvalidName] = useState(false);
  const [invalidEmail, setInvalidEmail] = useState(false);
  const [invalidPhone, setInvalidPhone] = useState(false);
  const [invalidPostalCode, setInvalidPostalCode] = useState(false);
  const [invalidCountry, setInvalidCountry] = useState(false);
  const [invalidProvince, setInvalidProvince] = useState(false);
  const language = i18n.language;

  // FLAT ROOF
  const { configurationState: flatRoofConfig } = useFlatRoof();
  const flatRoofConfigName = flatRoofConfig.humanReadableName ?? undefined;

  // CAVITY WALL
  const { configurationState: cavityWallConfig } = useCavityWall();
  const cavityWallConfigName = cavityWallConfig.humanReadableName ?? undefined;

  // SLOPED ROOF
  const { configurationState: slopedRoofConfig } = useSlopedRoof();
  const slopedRoofConfigName = slopedRoofConfig.humanReadableName ?? undefined;

  // VENTILATED WALL
  const { configurationState: ventilatedWallConfig } = useVentilatedWall();
  const ventilatedWallConfigName = ventilatedWallConfig.humanReadableName ?? undefined;

  // FLOOR
  const { configurationState: floorConfig } = useFloor();
  const floorConfigName = floorConfig.humanReadableName ?? undefined;

  // SOL
  const { configurationState: solConfig } = useSol();
  const solConfigName = solConfig.humanReadableName ?? undefined;

  // TOIT EN PENTE
  const { configurationState: toitEnPenteConfig } = useToitEnPente();
  const toitEnPenteConfigName = toitEnPenteConfig.humanReadableName ?? undefined;

  const humanReadableName = determineCofiguration();

  const provinceOptions = useMemo(() => {
    if (country) {
      const provinces: string[] = t(`modal.country.${country}.provinces`, "").split(";").filter((p: string) => p !== "");
      if (provinces) return provinces;
    }
    return [];
  }, [t, country]);

  function determineCofiguration() {
    // FLAT ROOF
    if (flatRoofConfigName) {
      return flatRoofConfigName;
    }
    // CAVITY WALL
    if (cavityWallConfigName) {
      return cavityWallConfigName;
    }
    // SLOPED ROOF
    if (slopedRoofConfigName) {
      return slopedRoofConfigName;
    }
    // VENTILATED WALL
    if (ventilatedWallConfigName) {
      return ventilatedWallConfigName;
    }
    // FLOOR
    if (floorConfigName) {
      return floorConfigName;
    }
    // SOL
    if (solConfigName) {
      return solConfigName;
    }
    // TOIT EN PENTE
    if (toitEnPenteConfigName) {
      return toitEnPenteConfigName;
    }
  }

  function handleNameChange(e: ChangeEvent<HTMLInputElement>) {
    setInvalidName(false);
    setName(e.target.value);
  }

  function handleEmailChange(e: ChangeEvent<HTMLInputElement>) {
    setInvalidEmail(false);
    setEmail(e.target.value);
  }

  function handlePhoneChange(e: ChangeEvent<HTMLInputElement>) {
    setInvalidPhone(false);
    setPhone(e.target.value);
  }

  function handlePostalCodeChange(e: ChangeEvent<HTMLInputElement>) {
    setInvalidPostalCode(false);
    setPostalCode(e.target.value);
  }

  function handleCountryChange(e: ChangeEvent<HTMLSelectElement>) {
    setInvalidCountry(false);
    setCountry(e.target.value);
    setInvalidProvince(false);
    setProvince("");
  }

  function handleProvinceChange(e: ChangeEvent<HTMLSelectElement>) {
    setInvalidProvince(false);
    setProvince(e.target.value);
  }

  function validate(): boolean {
    const validMail = validationRegex.email.test(String(email).toLowerCase());
    const validName = name !== "";
    const validPhone = validationRegex.phone.test(phone);
    const validPostalCode = postalCode !== "";
    const validCountry = country !== "";
    const validProvince = provinceOptions.length > 0 ? province !== "" : true;

    if (!validName) {
      setInvalidName(true);
    }

    if (!validMail) {
      setInvalidEmail(true);
    }

    if (!validPhone) {
      setInvalidPhone(true);
    }

    if (!validPostalCode) {
      setInvalidPostalCode(true);
    }

    if (!validCountry) {
      setInvalidCountry(true);
    }

    if (!validProvince) {
      setInvalidProvince(true);
    }

    return validName && validMail && validPhone && validPostalCode && validCountry && validProvince;
  }

  function submitRequest() {
    const dto: RequestAdviceDTO = {
      name,
      email,
      phone,
      language,
      humanReadableName,
      country,
      configurationUrl: window.location.href,
    };

    if (postalCode !== "") {
      dto.postalCode = postalCode;
    }
    if (company !== "") {
      dto.company = company;
    }
    if (message !== "") {
      dto.message = message;
    }
    if (province !== "") {
      dto.province = province;
    }

    api
      .postRequestAdvice(dto)
      .then((_) => {
        displayRequestAdviceSuccessModal();
      })
      .catch((_) => {
        closeModal();
      });
  }

  function handleSubmit(e: SyntheticEvent) {
    if (validate()) {
      e.currentTarget.className += " clicked";
      e.currentTarget.setAttribute("disabled", "disabled");

      submitRequest();
    }
  }

  return (
    <StandardModal
      titleIcon={CalculatorIcon}
      title={t("modal.requestAdvice.title")}
      subtitle={t("modal.requestAdvice.subtitle")}
      buttonText={t("modal.requestAdvice.button")}
      handleSubmit={handleSubmit}
      classnames={"advice standard-modal"}
    >
      <Form onSubmit={handleSubmit}>
        <Label htmlFor="name">
          {t("modal.requestAdvice.nameLabel")}
          <Required>*</Required>
        </Label>
        <Input
          invalid={invalidName}
          type="text"
          value={name}
          onChange={handleNameChange}
        />
        {invalidName && (
          <P fontSizePx={12} color={styles.colors.white65} lineHeightPx={20}>
            {t("modal.requestAdvice.invalidName")}
          </P>
        )}
        <Label htmlFor="email">
          {t("modal.requestAdvice.emailLabel")}
          <Required>*</Required>
        </Label>
        <Input
          invalid={invalidEmail}
          type="email"
          value={email}
          onChange={handleEmailChange}
          required
        />
        {invalidEmail && (
          <P fontSizePx={12} color={styles.colors.white65} lineHeightPx={20}>
            {t("modal.requestAdvice.invalidEmail")}
          </P>
        )}
        <LocationRow>
          <LocationContainer>
            <Label htmlFor="country">
              {t("modal.requestAdvice.countryLabel")}
              <Required>*</Required>
            </Label>
            <Input
              as="select"
              invalid={invalidCountry}
              value={country}
              onChange={handleCountryChange}
              required
            >
              {COUNTRY_OPTIONS.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.name}
                </option>
              ))}
            </Input>
            {invalidCountry && (
              <P fontSizePx={12} color={styles.colors.white65} lineHeightPx={20}>
                {t("modal.requestAdvice.invalidCountry")}
              </P>
            )}
          </LocationContainer>
          {provinceOptions.length > 0 && (
            <>
              <SpacerContainer />
              <LocationContainer>
                <Label htmlFor="province">
                  {t("modal.requestAdvice.provinceLabel")}
                  <Required>*</Required>
                </Label>
                <Input
                  as="select"
                  invalid={invalidProvince}
                  value={province || ""}
                  onChange={handleProvinceChange}
                  required
                >
                  <option value="" disabled></option>
                  {provinceOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Input>
                {invalidProvince && (
                  <P fontSizePx={12} color={styles.colors.white65} lineHeightPx={20}>
                    {t("modal.requestAdvice.invalidProvince")}
                  </P>
                )}
              </LocationContainer>
            </>
          )}
        </LocationRow>
        <PhoneRow>
          <PhoneContainer>
            <Label htmlFor="phone">
              {t("modal.requestAdvice.phoneLabel")}
              <Required>*</Required>
            </Label>
            <Input
              invalid={invalidPhone}
              type="text"
              value={phone}
              onChange={handlePhoneChange}
              required
            />
            {invalidPhone && (
              <P
                fontSizePx={12}
                color={styles.colors.white65}
                lineHeightPx={20}
              >
                {t("modal.requestAdvice.invalidPhone")}
              </P>
            )}
          </PhoneContainer>
          <SpacerContainer />
          <PostalCodeContainer>
            <Label htmlFor="postalCode">
              {t("modal.requestAdvice.postalCodeLabel")}
              <Required>*</Required>
            </Label>
            <Input
              invalid={invalidPostalCode}
              type="text"
              value={postalCode}
              onChange={handlePostalCodeChange}
            />
          </PostalCodeContainer>
        </PhoneRow>
        <Label htmlFor="company">{t("modal.requestAdvice.companyLabel")}</Label>
        <Input
          invalid={false}
          type="text"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
        />
        <Label htmlFor="message">{t("modal.requestAdvice.messageLabel")}</Label>
        <TextArea
          invalid={false}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
      </Form>
    </StandardModal>
  );
};
